import type {
  CompanyOccupantList,
  CurrentSession,
  CustomerView,
  DuplicateWarningList,
} from '@elite-residence/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type FormEvent, useContext, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { Alert, Badge, Field, Loading, QueryFailure, readResponse, useAction, useReadAction } from '../ui/ui';
import { loadCustomers } from './loadCustomers';

// Customers (V1-3-01 to V1-3-11): search and reuse, creation with
// non-blocking duplicate warnings, masked identities with an audited reveal,
// Company representatives and Occupants, history, Merge and archive. The
// server enforces every rule and permission; the UI only hides actions the
// session cannot perform. A revealed identity number lives in component
// state only and is never cached.

type Customer = CustomerView['customer'];
type Identity = Customer['identities'][number];
type Warning = DuplicateWarningList['warnings'][number];

const text: Dictionary<
  | 'customers'
  | 'search'
  | 'searchText'
  | 'phone'
  | 'country'
  | 'countryHint'
  | 'email'
  | 'find'
  | 'identitySearch'
  | 'identityType'
  | 'issuer'
  | 'number'
  | 'noResults'
  | 'reference'
  | 'name'
  | 'type'
  | 'individual'
  | 'company'
  | 'newIndividual'
  | 'newCompany'
  | 'fullName'
  | 'nationality'
  | 'none'
  | 'create'
  | 'companyName'
  | 'companyPhone'
  | 'companyEmail'
  | 'vat'
  | 'representative'
  | 'roleTitle'
  | 'warnings'
  | 'warningsHint'
  | 'continueAnyway'
  | 'warningReason'
  | 'archived'
  | 'merged'
  | 'contacts'
  | 'primary'
  | 'remove'
  | 'addPhone'
  | 'addEmail'
  | 'add'
  | 'identities'
  | 'reveal'
  | 'hide'
  | 'recordIdentity'
  | 'record'
  | 'markErroneous'
  | 'reason'
  | 'reasonRequired'
  | 'registration'
  | 'occupants'
  | 'occupantsFailed'
  | 'editOccupant'
  | 'save'
  | 'addOccupant'
  | 'replaceRepresentative'
  | 'history'
  | 'archive'
  | 'restore'
  | 'merge'
  | 'mergeHint'
  | 'master'
  | 'verification'
  | 'takeName'
  | 'readiness'
  | 'ready'
  | 'gapNationality'
  | 'gapIdentity'
  | 'identityInUse'
  | 'contactRequired'
  | 'phoneRequired'
  | 'saved'
  | 'back'
  | 'state'
  | 'valid_current'
  | 'valid_historical'
  | 'erroneous'
  | 'reused'
  | 'documents'
  | 'noDocuments'
  | 'uploadDocument'
  | 'documentKind'
  | 'identity_image'
  | 'company_legal'
  | 'customer_other'
  | 'forIdentity'
  | 'file'
  | 'fileHint'
  | 'upload'
  | 'download'
  | 'badFile'
> = {
  en: {
    customers: 'Customers',
    search: 'Search',
    searchText: 'Name or reference',
    phone: 'Phone',
    country: 'Country (for a national number)',
    countryHint: 'Two letters, e.g. SA. Not needed when the number starts with + or 00.',
    email: 'Email',
    find: 'Find',
    identitySearch: 'Find by complete identity',
    identityType: 'Identity type',
    issuer: 'Issuing country / authority',
    number: 'Number',
    noResults: 'No matching Customer.',
    reference: 'Reference',
    name: 'Name',
    type: 'Type',
    individual: 'Individual',
    company: 'Company',
    newIndividual: 'New Individual',
    newCompany: 'New Company',
    fullName: 'Full name',
    nationality: 'Nationality',
    none: '—',
    create: 'Create',
    companyName: 'Company name',
    companyPhone: 'Company phone',
    companyEmail: 'Company email',
    vat: 'VAT number',
    representative: 'Primary representative',
    roleTitle: 'Title',
    warnings: 'Possible duplicates',
    warningsHint:
      'These are warnings only. Reuse an existing Customer, or continue if this is a different person or Company.',
    continueAnyway: 'Continue anyway',
    warningReason: 'Why this is not a duplicate (optional)',
    archived: 'Archived',
    merged: 'Merged into another Customer',
    contacts: 'Contacts',
    primary: 'Primary',
    remove: 'Remove',
    addPhone: 'Add phone',
    addEmail: 'Add email',
    add: 'Add',
    identities: 'Identities',
    reveal: 'Show number',
    hide: 'Hide',
    recordIdentity: 'Record identity',
    record: 'Record',
    markErroneous: 'Mark erroneous',
    reason: 'Reason',
    reasonRequired: 'A reason is required.',
    registration: 'Commercial registration',
    occupants: 'Occupants',
    occupantsFailed: 'The Company Occupants could not be loaded.',
    editOccupant: 'Edit Occupant profile',
    save: 'Save',
    addOccupant: 'Add Occupant',
    replaceRepresentative: 'Replace primary representative',
    history: 'History',
    archive: 'Archive',
    restore: 'Restore',
    merge: 'Merge into a master Customer',
    mergeHint:
      'Only for the same real person or Company. This Customer stays as a historical reference; nothing is deleted.',
    master: 'Master Customer ID',
    verification: 'Verification reference',
    takeName: 'Keep this Customer’s name on the master',
    readiness: 'Before a Contract can be sent',
    ready: 'Customer data is complete for Contract Send.',
    gapNationality: 'Nationality is missing.',
    gapIdentity: 'A current identity (type, issuer and number) is missing.',
    identityInUse: 'This identity already belongs to another Customer.',
    contactRequired: 'An Individual keeps at least one phone and one email.',
    phoneRequired: 'Give a Company phone or a representative phone.',
    saved: 'Saved.',
    back: 'Back to Customers',
    state: 'State',
    valid_current: 'Current',
    valid_historical: 'Historical',
    erroneous: 'Erroneous',
    reused: 'This Occupant already exists in this Company and was reused.',
    documents: 'Documents',
    noDocuments: 'No documents yet.',
    uploadDocument: 'Upload a document',
    documentKind: 'Document',
    identity_image: 'Identity document image',
    company_legal: 'Company legal document',
    customer_other: 'Other Customer document',
    forIdentity: 'For identity',
    file: 'File',
    fileHint: 'PDF, JPEG or PNG, up to 10 MB. Every file is checked before it is stored.',
    upload: 'Upload',
    download: 'Download',
    badFile:
      'The file was refused: its type is not accepted, its content does not match its type, or it failed the security check. Nothing was stored.',
  },
  ar: {
    customers: 'العملاء',
    search: 'البحث',
    searchText: 'الاسم أو المرجع',
    phone: 'الهاتف',
    country: 'الدولة (للرقم المحلي)',
    countryHint: 'حرفان، مثل SA. غير مطلوب إذا بدأ الرقم بـ + أو 00.',
    email: 'البريد الإلكتروني',
    find: 'بحث',
    identitySearch: 'البحث بهوية كاملة',
    identityType: 'نوع الهوية',
    issuer: 'دولة / جهة الإصدار',
    number: 'الرقم',
    noResults: 'لا يوجد عميل مطابق.',
    reference: 'المرجع',
    name: 'الاسم',
    type: 'النوع',
    individual: 'فرد',
    company: 'شركة',
    newIndividual: 'عميل فرد جديد',
    newCompany: 'شركة جديدة',
    fullName: 'الاسم الكامل',
    nationality: 'الجنسية',
    none: '—',
    create: 'إنشاء',
    companyName: 'اسم الشركة',
    companyPhone: 'هاتف الشركة',
    companyEmail: 'بريد الشركة',
    vat: 'الرقم الضريبي',
    representative: 'الممثل الرئيسي',
    roleTitle: 'المسمى',
    warnings: 'تكرار محتمل',
    warningsHint: 'هذه تنبيهات فقط. أعد استخدام عميل موجود، أو تابع إذا كان شخصًا أو شركة مختلفة.',
    continueAnyway: 'المتابعة على أي حال',
    warningReason: 'سبب عدم كونه مكررًا (اختياري)',
    archived: 'مؤرشف',
    merged: 'مدمج في عميل آخر',
    contacts: 'وسائل الاتصال',
    primary: 'رئيسي',
    remove: 'إزالة',
    addPhone: 'إضافة هاتف',
    addEmail: 'إضافة بريد',
    add: 'إضافة',
    identities: 'الهويات',
    reveal: 'إظهار الرقم',
    hide: 'إخفاء',
    recordIdentity: 'تسجيل هوية',
    record: 'تسجيل',
    markErroneous: 'تعليم كخاطئة',
    reason: 'السبب',
    reasonRequired: 'السبب مطلوب.',
    registration: 'السجل التجاري',
    occupants: 'الشاغلون',
    occupantsFailed: 'تعذر تحميل شاغلي الشركة.',
    editOccupant: 'تعديل بيانات الشاغل',
    save: 'حفظ',
    addOccupant: 'إضافة شاغل',
    replaceRepresentative: 'استبدال الممثل الرئيسي',
    history: 'السجل',
    archive: 'أرشفة',
    restore: 'استعادة',
    merge: 'دمج في عميل رئيسي',
    mergeHint: 'فقط لنفس الشخص أو الشركة. يبقى هذا العميل مرجعًا تاريخيًا ولا يُحذف شيء.',
    master: 'معرّف العميل الرئيسي',
    verification: 'مرجع التحقق',
    takeName: 'الإبقاء على اسم هذا العميل في العميل الرئيسي',
    readiness: 'قبل إرسال العقد',
    ready: 'بيانات العميل مكتملة لإرسال العقد.',
    gapNationality: 'الجنسية غير مسجلة.',
    gapIdentity: 'لا توجد هوية حالية (النوع والجهة والرقم).',
    identityInUse: 'هذه الهوية مسجلة لعميل آخر.',
    contactRequired: 'يحتفظ العميل الفرد بهاتف وبريد إلكتروني على الأقل.',
    phoneRequired: 'أدخل هاتف الشركة أو هاتف الممثل.',
    saved: 'تم الحفظ.',
    back: 'العودة إلى العملاء',
    state: 'الحالة',
    valid_current: 'حالية',
    valid_historical: 'سابقة',
    erroneous: 'خاطئة',
    reused: 'هذا الشاغل موجود في هذه الشركة وأُعيد استخدامه.',
    documents: 'المستندات',
    noDocuments: 'لا توجد مستندات بعد.',
    uploadDocument: 'رفع مستند',
    documentKind: 'المستند',
    identity_image: 'صورة وثيقة الهوية',
    company_legal: 'مستند قانوني للشركة',
    customer_other: 'مستند آخر للعميل',
    forIdentity: 'للهوية',
    file: 'الملف',
    fileHint: 'PDF أو JPEG أو PNG، حتى 10 ميجابايت. يُفحص كل ملف قبل حفظه.',
    upload: 'رفع',
    download: 'تنزيل',
    badFile: 'رُفض الملف: نوعه غير مقبول، أو محتواه لا يطابق نوعه، أو لم يجتز الفحص الأمني. لم يُحفظ شيء.',
  },
};

const has = (session: CurrentSession, key: string) => session.permissions.includes(key);
const allBranches = (session: CurrentSession) => session.scope.type === 'all_branches';
// AD-5 fail-closed (pending HAA): identity view/edit count only with All Branches.
// AD-5 (approved): All-Branches holders act directly; a One-Branch holder
// acts through a Booking of this Customer (opened from the Booking page with
// ?bookingId=). The server checks the context; the UI only offers it.
const BookingContext = createContext<string | null>(null);
const useBookingContext = () => useContext(BookingContext);
const withBooking = (bookingId: string | null) => (bookingId ? { bookingId } : {});
const identityKey = (
  session: CurrentSession,
  key: 'customer.identity_view' | 'customer.identity_edit' | 'customer.documents_view' | 'customer.documents_manage',
  bookingId: string | null,
) => has(session, key) && (allBranches(session) || bookingId !== null);

// Phone input: a national number needs a country context (never guessed).
const phoneOf = (number: string, country: string) =>
  number.trim()
    ? { number: number.trim(), ...(country.trim() ? { country: country.trim().toUpperCase() } : {}) }
    : undefined;

function useReference(kind: 'nationality' | 'identity_type' | 'identity_issuer') {
  const client = useApiClient();
  return useQuery({
    queryKey: ['reference', kind],
    queryFn: async ({ signal }) => {
      const r = await client.listReferenceValues({ path: { kind } }, { signal });
      return readResponse(r).values;
    },
  });
}

function ReferenceSelect({
  id,
  kind,
  value,
  onChange,
  optional,
}: {
  readonly id: string;
  readonly kind: 'nationality' | 'identity_type' | 'identity_issuer';
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly optional?: boolean;
}) {
  const values = useReference(kind);
  const { locale } = useLocale();
  if (values.isError) return <QueryFailure query={values} />;
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{optional ? '—' : ''}</option>
      {(values.data ?? []).map((v) => (
        <option key={v.id} value={v.id}>
          {v.labels[locale]}
        </option>
      ))}
    </select>
  );
}

function CustomerTable({ customers }: { readonly customers: readonly Customer[] }) {
  const t = useText(text);
  if (customers.length === 0) return <p>{t.noResults}</p>;
  return (
    <table className="er-table">
      <thead>
        <tr>
          <th>{t.reference}</th>
          <th>{t.name}</th>
          <th>{t.type}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {customers.map((c) => (
          <tr key={c.id}>
            <td className="er-mono">{c.displayReference}</td>
            <td>
              <Link to={`/customers/${c.id}`}>{c.name}</Link>
            </td>
            <td>{c.type === 'individual' ? t.individual : t.company}</td>
            <td>
              {c.archived && <Badge tone="off">{t.archived}</Badge>}
              {c.mergedIntoCustomerId && <Badge tone="warn">{t.merged}</Badge>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Warnings({
  warnings,
  reason,
  setReason,
}: {
  readonly warnings: readonly Warning[];
  readonly reason: string;
  readonly setReason: (value: string) => void;
}) {
  const t = useText(text);
  const { locale } = useLocale();
  return (
    <section className="er-card" aria-label={t.warnings}>
      <h3>{t.warnings}</h3>
      <p>{t.warningsHint}</p>
      <ul>
        {warnings.map((w) => (
          <li key={`${w.kind}-${w.customerId}`}>
            <Link to={`/customers/${w.customerId}`}>
              {w.displayReference} — {w.name}
            </Link>{' '}
            ({WARNING_KINDS[w.kind]?.[locale] ?? w.kind})
          </li>
        ))}
      </ul>
      <Field id="warning-reason" label={t.warningReason}>
        <input id="warning-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </section>
  );
}

const WARNING_KINDS: Readonly<Record<string, Readonly<Record<'ar' | 'en', string>>>> = {
  phone: { en: 'same phone', ar: 'نفس الهاتف' },
  email: { en: 'same email', ar: 'نفس البريد' },
  name: { en: 'same name', ar: 'نفس الاسم' },
  vat: { en: 'same VAT number', ar: 'نفس الرقم الضريبي' },
};

export function CustomersPage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const navigate = useNavigate();
  const [query, setQuery] = useState({ text: '', phone: '', phoneCountry: '', email: '' });
  const [results, setResults] = useState<readonly Customer[] | null>(null);
  const [identity, setIdentity] = useState({ identityTypeId: '', issuerId: '', number: '' });
  const search = useReadAction();
  const identitySearch = useReadAction();

  async function find(event: FormEvent) {
    event.preventDefault();
    const q = Object.fromEntries(Object.entries(query).filter(([, v]) => v.trim() !== '')) as typeof query;
    // Both searches publish into one result list: only the latest one may.
    identitySearch.cancel();
    setResults(null);
    const r = await search.run(() => loadCustomers(client, q));
    if (r?.status === 200) setResults(r.body.customers);
  }

  async function findByIdentity(event: FormEvent) {
    event.preventDefault();
    search.cancel();
    setResults(null);
    const r = await identitySearch.run(() => client.searchCustomersByIdentity({ body: identity }));
    if (r?.status === 200) {
      setResults(r.body.customers);
      // The number is not kept after the search.
      setIdentity({ ...identity, number: '' });
    }
  }

  return (
    <main>
      <h1>{t.customers}</h1>
      <section className="er-card">
        <h2>{t.search}</h2>
        {search.error && <Alert kind="error">{search.error}</Alert>}
        <form onSubmit={find} noValidate>
          <Field id="search-text" label={t.searchText}>
            <input id="search-text" value={query.text} onChange={(e) => setQuery({ ...query, text: e.target.value })} />
          </Field>
          <Field id="search-phone" label={t.phone}>
            <input
              id="search-phone"
              dir="ltr"
              value={query.phone}
              onChange={(e) => setQuery({ ...query, phone: e.target.value })}
            />
          </Field>
          <Field id="search-country" label={t.country} hint={t.countryHint}>
            <input
              id="search-country"
              dir="ltr"
              maxLength={2}
              value={query.phoneCountry}
              onChange={(e) => setQuery({ ...query, phoneCountry: e.target.value })}
            />
          </Field>
          <Field id="search-email" label={t.email}>
            <input
              id="search-email"
              dir="ltr"
              value={query.email}
              onChange={(e) => setQuery({ ...query, email: e.target.value })}
            />
          </Field>
          <button type="submit" className="er-button" disabled={search.busy}>
            {t.find}
          </button>
        </form>
        <h3>{t.identitySearch}</h3>
        {identitySearch.error && <Alert kind="error">{identitySearch.error}</Alert>}
        <form onSubmit={findByIdentity} noValidate>
          <Field id="search-identity-type" label={t.identityType}>
            <ReferenceSelect
              id="search-identity-type"
              kind="identity_type"
              value={identity.identityTypeId}
              onChange={(v) => setIdentity({ ...identity, identityTypeId: v })}
            />
          </Field>
          <Field id="search-issuer" label={t.issuer}>
            <ReferenceSelect
              id="search-issuer"
              kind="identity_issuer"
              value={identity.issuerId}
              onChange={(v) => setIdentity({ ...identity, issuerId: v })}
            />
          </Field>
          <Field id="search-number" label={t.number}>
            <input
              id="search-number"
              dir="ltr"
              autoComplete="off"
              value={identity.number}
              onChange={(e) => setIdentity({ ...identity, number: e.target.value })}
            />
          </Field>
          <button type="submit" className="er-button" disabled={identitySearch.busy}>
            {t.find}
          </button>
        </form>
        {results && <CustomerTable customers={results} />}
      </section>
      {has(session, 'customer.profile_edit') && (
        <>
          <NewIndividual onCreated={(id) => navigate(`/customers/${id}`)} />
          <NewCompany onCreated={(id) => navigate(`/customers/${id}`)} />
        </>
      )}
    </main>
  );
}

function NewIndividual({ onCreated }: { readonly onCreated: (id: string) => void }) {
  const client = useApiClient();
  const t = useText(text);
  const [form, setForm] = useState({ fullName: '', phone: '', country: 'SA', email: '', nationalityId: '' });
  const [warnings, setWarnings] = useState<readonly Warning[] | null>(null);
  const [reason, setReason] = useState('');
  const action = useAction();

  async function create(event: FormEvent) {
    event.preventDefault();
    const phone = phoneOf(form.phone, form.country) ?? { number: '' };
    const body = {
      fullName: form.fullName,
      phone,
      email: form.email,
      ...(form.nationalityId ? { nationalityId: form.nationalityId } : {}),
      ...(warnings
        ? { continueDespiteWarnings: true, ...(reason.trim() ? { warningReason: reason.trim() } : {}) }
        : {}),
    };
    const r = await action.run(
      (key) => client.createIndividualCustomer({ headers: { 'idempotency-key': key }, body }),
      {
        duplicate_warnings: t.warningsHint,
      },
    );
    if (r && (r.status === 200 || r.status === 201)) {
      onCreated(r.body.customer.id);
      return;
    }
    // Show the warnings on a duplicate_warnings conflict.
    const w = await client.getDuplicateWarnings({
      body: { name: form.fullName, phones: [phone], emails: form.email ? [form.email] : [] },
    });
    if (w.status === 200 && w.body.warnings.length > 0) setWarnings(w.body.warnings);
  }

  return (
    <section className="er-card">
      <h2>{t.newIndividual}</h2>
      {action.error && !warnings && <Alert kind="error">{action.error}</Alert>}
      <form onSubmit={create} noValidate>
        <Field id="ind-name" label={t.fullName}>
          <input id="ind-name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
        </Field>
        <Field id="ind-phone" label={t.phone}>
          <input
            id="ind-phone"
            dir="ltr"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Field>
        <Field id="ind-country" label={t.country} hint={t.countryHint}>
          <input
            id="ind-country"
            dir="ltr"
            maxLength={2}
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
          />
        </Field>
        <Field id="ind-email" label={t.email}>
          <input
            id="ind-email"
            dir="ltr"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field id="ind-nationality" label={t.nationality}>
          <ReferenceSelect
            id="ind-nationality"
            kind="nationality"
            optional
            value={form.nationalityId}
            onChange={(v) => setForm({ ...form, nationalityId: v })}
          />
        </Field>
        {warnings && <Warnings warnings={warnings} reason={reason} setReason={setReason} />}
        <button type="submit" className="er-button" disabled={action.busy}>
          {warnings ? t.continueAnyway : t.create}
        </button>
      </form>
    </section>
  );
}

function NewCompany({ onCreated }: { readonly onCreated: (id: string) => void }) {
  const client = useApiClient();
  const t = useText(text);
  const [form, setForm] = useState({
    companyName: '',
    companyPhone: '',
    country: 'SA',
    companyEmail: '',
    vatNumber: '',
    repName: '',
    repPhone: '',
    repTitle: '',
  });
  const [warnings, setWarnings] = useState<readonly Warning[] | null>(null);
  const [reason, setReason] = useState('');
  const action = useAction();

  async function create(event: FormEvent) {
    event.preventDefault();
    const companyPhone = phoneOf(form.companyPhone, form.country);
    const repPhone = phoneOf(form.repPhone, form.country);
    const body = {
      companyName: form.companyName,
      ...(companyPhone ? { companyPhone } : {}),
      ...(form.companyEmail.trim() ? { companyEmail: form.companyEmail.trim() } : {}),
      ...(form.vatNumber.trim() ? { vatNumber: form.vatNumber.trim() } : {}),
      representative: {
        name: form.repName,
        ...(repPhone ? { phone: repPhone } : {}),
        ...(form.repTitle.trim() ? { roleTitle: form.repTitle.trim() } : {}),
      },
      ...(warnings
        ? { continueDespiteWarnings: true, ...(reason.trim() ? { warningReason: reason.trim() } : {}) }
        : {}),
    };
    const r = await action.run((key) => client.createCompanyCustomer({ headers: { 'idempotency-key': key }, body }), {
      duplicate_warnings: t.warningsHint,
    });
    if (r && (r.status === 200 || r.status === 201)) {
      onCreated(r.body.customer.id);
      return;
    }
    const w = await client.getDuplicateWarnings({
      body: {
        name: form.companyName,
        phones: [companyPhone, repPhone].filter((p) => p !== undefined),
        ...(form.vatNumber.trim() ? { vatNumber: form.vatNumber.trim() } : {}),
      },
    });
    if (w.status === 200 && w.body.warnings.length > 0) setWarnings(w.body.warnings);
  }

  const set = (field: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [field]: e.target.value });
  return (
    <section className="er-card">
      <h2>{t.newCompany}</h2>
      {action.error && !warnings && <Alert kind="error">{action.error}</Alert>}
      <form onSubmit={create} noValidate>
        <Field id="co-name" label={t.companyName}>
          <input id="co-name" value={form.companyName} onChange={set('companyName')} />
        </Field>
        <Field id="co-phone" label={t.companyPhone} hint={t.phoneRequired}>
          <input id="co-phone" dir="ltr" value={form.companyPhone} onChange={set('companyPhone')} />
        </Field>
        <Field id="co-country" label={t.country} hint={t.countryHint}>
          <input id="co-country" dir="ltr" maxLength={2} value={form.country} onChange={set('country')} />
        </Field>
        <Field id="co-email" label={t.companyEmail}>
          <input id="co-email" dir="ltr" value={form.companyEmail} onChange={set('companyEmail')} />
        </Field>
        <Field id="co-vat" label={t.vat}>
          <input id="co-vat" dir="ltr" value={form.vatNumber} onChange={set('vatNumber')} />
        </Field>
        <Field id="co-rep" label={`${t.representative} — ${t.fullName}`}>
          <input id="co-rep" value={form.repName} onChange={set('repName')} />
        </Field>
        <Field id="co-rep-phone" label={`${t.representative} — ${t.phone}`}>
          <input id="co-rep-phone" dir="ltr" value={form.repPhone} onChange={set('repPhone')} />
        </Field>
        <Field id="co-rep-title" label={`${t.representative} — ${t.roleTitle}`}>
          <input id="co-rep-title" value={form.repTitle} onChange={set('repTitle')} />
        </Field>
        {warnings && <Warnings warnings={warnings} reason={reason} setReason={setReason} />}
        <button type="submit" className="er-button" disabled={action.busy}>
          {warnings ? t.continueAnyway : t.create}
        </button>
      </form>
    </section>
  );
}

// ---- detail ---------------------------------------------------------------------------

export function CustomerDetailPage({ session }: { readonly session: CurrentSession }) {
  const { customerId = '' } = useParams();
  const [params] = useSearchParams();
  const bookingId = params.get('bookingId');
  const client = useApiClient();
  const t = useText(text);
  const customer = useQuery({
    queryKey: ['customer', customerId],
    queryFn: async ({ signal }) => {
      const r = await client.getCustomer({ path: { customerId } }, { signal });
      return readResponse(r).customer;
    },
  });
  if (customer.isPending) return <Loading />;
  if (customer.isError) return <QueryFailure query={customer} />;
  const c = customer.data;
  if (!c) {
    return (
      <main>
        <Alert kind="error">{t.noResults}</Alert>
        <Link to="/customers">{t.back}</Link>
      </main>
    );
  }
  const editable = has(session, 'customer.profile_edit') && !c.mergedIntoCustomerId;
  return (
    <BookingContext.Provider value={bookingId}>
      <main>
        <Link to="/customers">{t.back}</Link>
        <h1>
          {c.name} <span className="er-mono">{c.displayReference}</span>
        </h1>
        <p>
          {c.type === 'individual' ? t.individual : t.company} {c.archived && <Badge tone="off">{t.archived}</Badge>}{' '}
          {c.mergedIntoCustomerId && (
            <Link to={`/customers/${c.mergedIntoCustomerId}`}>
              <Badge tone="warn">{t.merged}</Badge>
            </Link>
          )}
        </p>
        <Readiness customer={c} />
        <Contacts customer={c} editable={editable} />
        {c.type === 'individual' && (
          <Identities
            customer={c}
            identities={c.identities}
            canView={identityKey(session, 'customer.identity_view', bookingId)}
            canEdit={identityKey(session, 'customer.identity_edit', bookingId) && !c.mergedIntoCustomerId}
          />
        )}
        {c.type === 'company' && <CompanySections customer={c} session={session} editable={editable} />}
        {identityKey(session, 'customer.documents_view', bookingId) && (
          <Documents
            customer={c}
            canManage={identityKey(session, 'customer.documents_manage', bookingId) && !c.mergedIntoCustomerId}
          />
        )}
        {has(session, 'customer.profile_edit') && <History customerId={c.id} />}
        {has(session, 'customer.archive') && allBranches(session) && <ArchiveSection customer={c} />}
        {has(session, 'customer.merge') &&
          identityKey(session, 'customer.identity_view', bookingId) &&
          allBranches(session) &&
          !c.mergedIntoCustomerId && <MergeSection customer={c} />}
      </main>
    </BookingContext.Provider>
  );
}

function Readiness({ customer }: { readonly customer: Customer }) {
  const t = useText(text);
  const gaps = customer.contractSendGaps;
  return (
    <section className="er-card">
      <h2>{t.readiness}</h2>
      {gaps.length === 0 ? (
        <p>{t.ready}</p>
      ) : (
        <ul>
          {gaps.map((g) => (
            <li key={g}>{g === 'nationality' ? t.gapNationality : t.gapIdentity}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function useRefresh(customerId: string) {
  const queries = useQueryClient();
  return () => queries.invalidateQueries({ queryKey: ['customer', customerId] });
}

function Contacts({ customer, editable }: { readonly customer: Customer; readonly editable: boolean }) {
  const client = useApiClient();
  const t = useText(text);
  const refresh = useRefresh(customer.id);
  const action = useAction();
  const [phone, setPhone] = useState({ number: '', country: 'SA' });
  const [email, setEmail] = useState('');

  async function change(body: Record<string, unknown>) {
    const r = await action.run(
      (key) =>
        client.updateCustomerProfile({
          path: { customerId: customer.id },
          headers: { 'idempotency-key': key },
          body,
        }),
      { contact_required: t.contactRequired },
    );
    if (r) {
      setPhone({ number: '', country: phone.country });
      setEmail('');
      await refresh();
    }
  }

  return (
    <section className="er-card">
      <h2>{t.contacts}</h2>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      <ul>
        {customer.contacts.map((contact) => (
          <li key={contact.id}>
            <span dir="ltr">{contact.value}</span> {contact.primary && <Badge tone="ok">{t.primary}</Badge>}{' '}
            {editable && (
              <button
                type="button"
                className="er-link-button"
                disabled={action.busy}
                onClick={() => change({ removeContactId: contact.id })}
              >
                {t.remove}
              </button>
            )}
          </li>
        ))}
      </ul>
      {editable && (
        <div className="er-actions">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const p = phoneOf(phone.number, phone.country);
              if (p) void change({ addPhone: p });
            }}
            noValidate
          >
            <Field id="add-phone" label={t.addPhone}>
              <input
                id="add-phone"
                dir="ltr"
                value={phone.number}
                onChange={(e) => setPhone({ ...phone, number: e.target.value })}
              />
            </Field>
            <Field id="add-phone-country" label={t.country}>
              <input
                id="add-phone-country"
                dir="ltr"
                maxLength={2}
                value={phone.country}
                onChange={(e) => setPhone({ ...phone, country: e.target.value })}
              />
            </Field>
            <button type="submit" className="er-button" disabled={action.busy}>
              {t.add}
            </button>
          </form>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim()) void change({ addEmail: email.trim() });
            }}
            noValidate
          >
            <Field id="add-email" label={t.addEmail}>
              <input id="add-email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <button type="submit" className="er-button" disabled={action.busy}>
              {t.add}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}

function IdentityRow({
  identity,
  canView,
  canEdit,
  onChanged,
}: {
  readonly identity: Identity;
  readonly canView: boolean;
  readonly canEdit: boolean;
  readonly onChanged: () => void;
}) {
  const bookingId = useBookingContext();
  const client = useApiClient();
  const t = useText(text);
  const types = useReference('identity_type');
  const issuers = useReference('identity_issuer');
  const { locale } = useLocale();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const action = useAction();
  const label = (list: typeof types.data, id: string | null) =>
    id === null ? t.registration : ((list ?? []).find((v) => v.id === id)?.labels[locale] ?? t.none);

  async function reveal() {
    const r = await action.run(() =>
      client.revealCustomerIdentity({ path: { identityId: identity.id }, query: withBooking(bookingId) }),
    );
    if (r?.status === 200) setRevealed(r.body.number);
  }

  async function markErroneous() {
    if (!reason.trim()) {
      action.setError(t.reasonRequired);
      return;
    }
    const r = await action.run((key) =>
      client.changeCustomerIdentityState({
        path: { identityId: identity.id },
        headers: { 'idempotency-key': key },
        body: { state: 'erroneous', reason: reason.trim(), ...withBooking(bookingId) },
      }),
    );
    if (r) onChanged();
  }

  return (
    <tr>
      <td>{label(types.data, identity.identityTypeId)}</td>
      <td>{label(issuers.data, identity.issuerId)}</td>
      <td className="er-mono" dir="ltr">
        {revealed ?? identity.masked}
      </td>
      <td>{t[identity.state]}</td>
      <td>
        {action.error && <Alert kind="error">{action.error}</Alert>}
        {canView &&
          (revealed ? (
            <button type="button" className="er-link-button" onClick={() => setRevealed(null)}>
              {t.hide}
            </button>
          ) : (
            <button type="button" className="er-link-button" disabled={action.busy} onClick={reveal}>
              {t.reveal}
            </button>
          ))}
        {canEdit && identity.state !== 'erroneous' && (
          <span>
            {' '}
            <input aria-label={t.reason} value={reason} onChange={(e) => setReason(e.target.value)} />
            <button type="button" className="er-link-button" disabled={action.busy} onClick={markErroneous}>
              {t.markErroneous}
            </button>
          </span>
        )}
      </td>
    </tr>
  );
}

function Identities({
  customer,
  identities,
  canView,
  canEdit,
  occupantPersonId,
  registration,
}: {
  readonly customer: Customer;
  readonly identities: readonly Identity[];
  readonly canView: boolean;
  readonly canEdit: boolean;
  readonly occupantPersonId?: string;
  readonly registration?: boolean;
}) {
  const bookingId = useBookingContext();
  const client = useApiClient();
  const t = useText(text);
  const refresh = useRefresh(customer.id);
  const queries = useQueryClient();
  const action = useAction();
  const [form, setForm] = useState({ identityTypeId: '', issuerId: '', number: '' });
  const onChanged = () => {
    void refresh();
    void queries.invalidateQueries({ queryKey: ['customer-occupants', customer.id] });
  };

  async function record(event: FormEvent) {
    event.preventDefault();
    const specific = { identity_in_use: t.identityInUse, registration_in_use: t.identityInUse };
    const r = registration
      ? await action.run(
          (key) =>
            client.recordCompanyRegistration({
              path: { customerId: customer.id },
              headers: { 'idempotency-key': key },
              body: { issuerId: form.issuerId, number: form.number },
            }),
          specific,
        )
      : await action.run(
          (key) =>
            client.recordCustomerIdentity({
              path: { customerId: customer.id },
              headers: { 'idempotency-key': key },
              body: { ...form, ...(occupantPersonId ? { occupantPersonId } : {}), ...withBooking(bookingId) },
            }),
          specific,
        );
    if (r) {
      setForm({ identityTypeId: '', issuerId: '', number: '' });
      onChanged();
    }
  }

  const prefix = occupantPersonId ? `occ-${occupantPersonId.slice(0, 8)}` : registration ? 'reg' : 'idn';
  return (
    <section className="er-card">
      <h2>{registration ? t.registration : t.identities}</h2>
      {identities.length > 0 && (
        <table className="er-table">
          <thead>
            <tr>
              <th>{t.identityType}</th>
              <th>{t.issuer}</th>
              <th>{t.number}</th>
              <th>{t.state}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {identities.map((i) => (
              <IdentityRow key={i.id} identity={i} canView={canView} canEdit={canEdit} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      )}
      {canEdit && (
        <form onSubmit={record} noValidate>
          <h3>{t.recordIdentity}</h3>
          {action.error && <Alert kind="error">{action.error}</Alert>}
          {!registration && (
            <Field id={`${prefix}-type`} label={t.identityType}>
              <ReferenceSelect
                id={`${prefix}-type`}
                kind="identity_type"
                value={form.identityTypeId}
                onChange={(v) => setForm({ ...form, identityTypeId: v })}
              />
            </Field>
          )}
          <Field id={`${prefix}-issuer`} label={t.issuer}>
            <ReferenceSelect
              id={`${prefix}-issuer`}
              kind="identity_issuer"
              value={form.issuerId}
              onChange={(v) => setForm({ ...form, issuerId: v })}
            />
          </Field>
          <Field id={`${prefix}-number`} label={t.number}>
            <input
              id={`${prefix}-number`}
              dir="ltr"
              autoComplete="off"
              value={form.number}
              onChange={(e) => setForm({ ...form, number: e.target.value })}
            />
          </Field>
          <button type="submit" className="er-button" disabled={action.busy}>
            {t.record}
          </button>
        </form>
      )}
    </section>
  );
}

function CompanySections({
  customer,
  session,
  editable,
}: {
  readonly customer: Customer;
  readonly session: CurrentSession;
  readonly editable: boolean;
}) {
  const bookingId = useBookingContext();
  const client = useApiClient();
  const t = useText(text);
  const refresh = useRefresh(customer.id);
  const queries = useQueryClient();
  const occupants = useQuery({
    queryKey: ['customer-occupants', customer.id],
    queryFn: async ({ signal }) => {
      const r = await client.listCompanyOccupants({ path: { customerId: customer.id } }, { signal });
      if (r.status !== 200) throw new Error('company_occupants_unavailable');
      return r.body.occupants;
    },
  });
  const rep = customer.company?.primaryRepresentative;
  const repAction = useAction();
  const occAction = useAction();
  const [repForm, setRepForm] = useState({ name: '', phone: '', country: 'SA', reason: '' });
  const [occForm, setOccForm] = useState({ fullName: '', phone: '', country: 'SA', nationalityId: '' });
  const [reused, setReused] = useState(false);

  async function replaceRep(event: FormEvent) {
    event.preventDefault();
    const phone = phoneOf(repForm.phone, repForm.country);
    const r = await repAction.run(
      (key) =>
        client.replacePrimaryRepresentative({
          path: { customerId: customer.id },
          headers: { 'idempotency-key': key },
          body: {
            name: repForm.name,
            ...(phone ? { phone } : {}),
            ...(repForm.reason.trim() ? { reason: repForm.reason.trim() } : {}),
          },
        }),
      { phone_required: t.phoneRequired },
    );
    if (r) {
      setRepForm({ name: '', phone: '', country: repForm.country, reason: '' });
      await refresh();
    }
  }

  async function addOccupant(event: FormEvent) {
    event.preventDefault();
    const phone = phoneOf(occForm.phone, occForm.country);
    const r = await occAction.run((key) =>
      client.addCompanyOccupant({
        path: { customerId: customer.id },
        headers: { 'idempotency-key': key },
        body: {
          fullName: occForm.fullName,
          ...(phone ? { phone } : {}),
          ...(occForm.nationalityId ? { nationalityId: occForm.nationalityId } : {}),
        },
      }),
    );
    if (r && (r.status === 200 || r.status === 201)) {
      setReused(r.body.reused);
      setOccForm({ fullName: '', phone: '', country: occForm.country, nationalityId: '' });
      await queries.cancelQueries({ queryKey: ['customer-occupants', customer.id] });
      await queries.invalidateQueries({ queryKey: ['customer-occupants', customer.id] });
    }
  }

  return (
    <>
      <section className="er-card">
        <h2>{t.representative}</h2>
        {rep && (
          <p>
            {rep.name} {rep.roleTitle && `(${rep.roleTitle})`} <span dir="ltr">{rep.phone ?? ''}</span>
          </p>
        )}
        {editable && (
          <form onSubmit={replaceRep} noValidate>
            <h3>{t.replaceRepresentative}</h3>
            {repAction.error && <Alert kind="error">{repAction.error}</Alert>}
            <Field id="rep-name" label={t.fullName}>
              <input
                id="rep-name"
                value={repForm.name}
                onChange={(e) => setRepForm({ ...repForm, name: e.target.value })}
              />
            </Field>
            <Field id="rep-phone" label={t.phone}>
              <input
                id="rep-phone"
                dir="ltr"
                value={repForm.phone}
                onChange={(e) => setRepForm({ ...repForm, phone: e.target.value })}
              />
            </Field>
            <Field id="rep-reason" label={t.reason}>
              <input
                id="rep-reason"
                value={repForm.reason}
                onChange={(e) => setRepForm({ ...repForm, reason: e.target.value })}
              />
            </Field>
            <button type="submit" className="er-button" disabled={repAction.busy}>
              {t.replaceRepresentative}
            </button>
          </form>
        )}
      </section>
      <Identities
        customer={customer}
        identities={customer.company?.registrations ?? []}
        canView={identityKey(session, 'customer.identity_view', bookingId)}
        canEdit={editable}
        registration
      />
      <section className="er-card">
        <h2>{t.occupants}</h2>
        {occupants.isError ? (
          <Alert kind="error">{t.occupantsFailed}</Alert>
        ) : occupants.isPending ? (
          <Loading />
        ) : (
          (occupants.data ?? []).map((o) => (
            <div key={o.id}>
              <h3>{o.name}</h3>
              {editable && <OccupantProfileEditor key={o.id} customerId={customer.id} occupant={o} />}
              <Identities
                customer={customer}
                identities={o.identities}
                canView={identityKey(session, 'customer.identity_view', bookingId)}
                canEdit={identityKey(session, 'customer.identity_edit', bookingId) && !customer.mergedIntoCustomerId}
                occupantPersonId={o.personId}
              />
            </div>
          ))
        )}
        {editable && (
          <form onSubmit={addOccupant} noValidate>
            <h3>{t.addOccupant}</h3>
            {occAction.error && <Alert kind="error">{occAction.error}</Alert>}
            {reused && <Alert kind="info">{t.reused}</Alert>}
            <Field id="occ-name" label={t.fullName}>
              <input
                id="occ-name"
                value={occForm.fullName}
                onChange={(e) => setOccForm({ ...occForm, fullName: e.target.value })}
              />
            </Field>
            <Field id="occ-phone" label={t.phone}>
              <input
                id="occ-phone"
                dir="ltr"
                value={occForm.phone}
                onChange={(e) => setOccForm({ ...occForm, phone: e.target.value })}
              />
            </Field>
            <Field id="occ-nationality" label={t.nationality}>
              <ReferenceSelect
                id="occ-nationality"
                kind="nationality"
                optional
                value={occForm.nationalityId}
                onChange={(nationalityId) => setOccForm({ ...occForm, nationalityId })}
              />
            </Field>
            <button type="submit" className="er-button" disabled={occAction.busy}>
              {t.add}
            </button>
          </form>
        )}
      </section>
    </>
  );
}

function OccupantProfileEditor({
  customerId,
  occupant,
}: {
  readonly customerId: string;
  readonly occupant: CompanyOccupantList['occupants'][number];
}) {
  const client = useApiClient();
  const t = useText(text);
  const queries = useQueryClient();
  const action = useAction();
  const [name, setName] = useState(occupant.name);
  const [nationalityId, setNationalityId] = useState(occupant.nationalityId ?? '');
  const prefix = `occ-profile-${occupant.id}`;
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void action
          .run((key) =>
            client.updateCompanyOccupant({
              path: { customerId, occupantId: occupant.id },
              headers: { 'idempotency-key': key },
              body: { fullName: name, nationalityId: nationalityId || null },
            }),
          )
          .then(async (response) => {
            if (!response) return;
            await queries.cancelQueries({ queryKey: ['customer-occupants', customerId] });
            await queries.invalidateQueries({ queryKey: ['customer-occupants', customerId] });
            await queries.invalidateQueries({ queryKey: ['customer', customerId, 'history'] });
            await queries.invalidateQueries({ queryKey: ['contract'] });
          });
      }}
      noValidate
    >
      <h4>{t.editOccupant}</h4>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      <Field id={`${prefix}-name`} label={t.fullName}>
        <input id={`${prefix}-name`} value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
      <Field id={`${prefix}-nationality`} label={t.nationality}>
        <ReferenceSelect
          id={`${prefix}-nationality`}
          kind="nationality"
          optional
          value={nationalityId}
          onChange={setNationalityId}
        />
      </Field>
      <button className="er-button secondary" type="submit" disabled={action.busy}>
        {t.save}
      </button>
    </form>
  );
}

// Customer documents (V1-3-10). AD-5 fail-closed: All-Branches holders of
// the document keys only. Files go to the API as raw bytes; the server
// sniffs, scans and publishes them. Downloads are attachments from the API.
type DocumentKind = 'identity_image' | 'company_legal' | 'customer_other';
type UploadType = 'application/pdf' | 'image/jpeg' | 'image/png';
const UPLOAD_TYPES: readonly string[] = ['application/pdf', 'image/jpeg', 'image/png'];

function Documents({ customer, canManage }: { readonly customer: Customer; readonly canManage: boolean }) {
  const bookingId = useBookingContext();
  const client = useApiClient();
  const t = useText(text);
  const queries = useQueryClient();
  const action = useAction();
  const documents = useQuery({
    queryKey: ['customer-documents', customer.id],
    queryFn: async ({ signal }) => {
      const r = await client.listCustomerDocuments(
        { path: { customerId: customer.id }, query: withBooking(bookingId) },
        { signal },
      );
      return readResponse(r).documents;
    },
  });
  const identities = customer.type === 'individual' ? customer.identities : [];
  const kinds: DocumentKind[] =
    customer.type === 'company' ? ['company_legal', 'customer_other'] : ['identity_image', 'customer_other'];
  const [kind, setKind] = useState<DocumentKind>(kinds[0] as DocumentKind);
  const [identityId, setIdentityId] = useState('');
  const [file, setFile] = useState<File | null>(null);

  async function upload(event: FormEvent) {
    event.preventDefault();
    if (!file) return;
    const contentType = UPLOAD_TYPES.includes(file.type) ? (file.type as UploadType) : 'application/pdf';
    const body = new Uint8Array(await file.arrayBuffer());
    const r = await action.run(
      (key) =>
        client.uploadCustomerDocument({
          path: { customerId: customer.id },
          query: {
            kind,
            ...(kind === 'identity_image' && identityId ? { personIdentityId: identityId } : {}),
            ...withBooking(bookingId),
          },
          headers: { 'idempotency-key': key },
          body,
          contentType,
        }),
      { validation_failed: t.badFile },
    );
    if (r) {
      setFile(null);
      (event.target as HTMLFormElement).reset();
      await queries.invalidateQueries({ queryKey: ['customer-documents', customer.id] });
    }
  }

  return (
    <section className="er-card">
      <h2>{t.documents}</h2>
      {documents.isPending ? (
        <Loading />
      ) : documents.isError ? (
        <QueryFailure query={documents} />
      ) : (documents.data ?? []).length === 0 ? (
        <p>{t.noDocuments}</p>
      ) : (
        <table className="er-table">
          <tbody>
            {(documents.data ?? []).map((d) => (
              <tr key={d.versionId}>
                <td>{t[d.kind]}</td>
                <td className="er-mono">{d.createdAt.slice(0, 10)}</td>
                <td>{Math.ceil(d.byteSize / 1024)} KB</td>
                <td>
                  <a
                    href={`/api/v1/document-versions/${d.versionId}/content${bookingId ? `?bookingId=${bookingId}` : ''}`}
                    download
                  >
                    {t.download}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canManage && (
        <form onSubmit={upload} noValidate>
          <h3>{t.uploadDocument}</h3>
          {action.error && <Alert kind="error">{action.error}</Alert>}
          <Field id="doc-kind" label={t.documentKind}>
            <select id="doc-kind" value={kind} onChange={(e) => setKind(e.target.value as DocumentKind)}>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {t[k]}
                </option>
              ))}
            </select>
          </Field>
          {kind === 'identity_image' && (
            <Field id="doc-identity" label={t.forIdentity}>
              <select id="doc-identity" value={identityId} onChange={(e) => setIdentityId(e.target.value)}>
                <option value="" />
                {identities
                  .filter((i) => i.state !== 'erroneous')
                  .map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.masked}
                    </option>
                  ))}
              </select>
            </Field>
          )}
          <Field id="doc-file" label={t.file} hint={t.fileHint}>
            <input
              id="doc-file"
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Field>
          <button type="submit" className="er-button" disabled={action.busy || !file}>
            {t.upload}
          </button>
        </form>
      )}
    </section>
  );
}

function History({ customerId }: { readonly customerId: string }) {
  const client = useApiClient();
  const t = useText(text);
  const history = useQuery({
    queryKey: ['customer', customerId, 'history'],
    queryFn: async ({ signal }) => {
      const r = await client.getCustomerHistory({ path: { customerId } }, { signal });
      return readResponse(r).entries;
    },
  });
  return (
    <section className="er-card">
      <h2>{t.history}</h2>
      {history.isPending ? (
        <Loading />
      ) : history.isError ? (
        <QueryFailure query={history} />
      ) : (
        <table className="er-table">
          <tbody>
            {(history.data ?? []).map((entry) => (
              <tr key={`${entry.recordedAt}-${entry.changeType}`}>
                <td className="er-mono">{entry.recordedAt}</td>
                <td>{entry.changeType}</td>
                <td>
                  {entry.changes.map((c) => (
                    <div key={c.field}>
                      {c.field}: {c.before ?? t.none} → {c.after ?? t.none}
                    </div>
                  ))}
                </td>
                <td>{entry.reason ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ArchiveSection({ customer }: { readonly customer: Customer }) {
  const client = useApiClient();
  const t = useText(text);
  const refresh = useRefresh(customer.id);
  const action = useAction();
  const [reason, setReason] = useState('');
  async function toggle() {
    const r = await action.run((key) =>
      client.setCustomerArchived({
        path: { customerId: customer.id },
        headers: { 'idempotency-key': key },
        body: { archived: !customer.archived, ...(reason.trim() ? { reason: reason.trim() } : {}) },
      }),
    );
    if (r) {
      setReason('');
      await refresh();
    }
  }
  return (
    <section className="er-card">
      <h2>{customer.archived ? t.restore : t.archive}</h2>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      <Field id="archive-reason" label={t.reason}>
        <input id="archive-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <button type="button" className="er-button" disabled={action.busy} onClick={toggle}>
        {customer.archived ? t.restore : t.archive}
      </button>
    </section>
  );
}

function MergeSection({ customer }: { readonly customer: Customer }) {
  const client = useApiClient();
  const t = useText(text);
  const navigate = useNavigate();
  const action = useAction();
  const [form, setForm] = useState({ master: '', verification: '', reason: '', takeName: false });
  async function merge(event: FormEvent) {
    event.preventDefault();
    const r = await action.run((key) =>
      client.mergeCustomers({
        headers: { 'idempotency-key': key },
        body: {
          sourceCustomerId: customer.id,
          masterCustomerId: form.master.trim(),
          verificationReference: form.verification,
          reason: form.reason,
          takeNameFromSource: form.takeName,
        },
      }),
    );
    if (r && r.status === 200) navigate(`/customers/${r.body.customer.id}`);
  }
  return (
    <section className="er-card">
      <h2>{t.merge}</h2>
      <p>{t.mergeHint}</p>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      <form onSubmit={merge} noValidate>
        <Field id="merge-master" label={t.master}>
          <input
            id="merge-master"
            dir="ltr"
            value={form.master}
            onChange={(e) => setForm({ ...form, master: e.target.value })}
          />
        </Field>
        <Field id="merge-verification" label={t.verification}>
          <input
            id="merge-verification"
            value={form.verification}
            onChange={(e) => setForm({ ...form, verification: e.target.value })}
          />
        </Field>
        <Field id="merge-reason" label={t.reason}>
          <input id="merge-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
        <label>
          <input
            type="checkbox"
            checked={form.takeName}
            onChange={(e) => setForm({ ...form, takeName: e.target.checked })}
          />{' '}
          {t.takeName}
        </label>
        <button type="submit" className="er-button" disabled={action.busy}>
          {t.merge}
        </button>
      </form>
    </section>
  );
}
